import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { api_routes, db_tables, features } from '@app/consts';
import { FeatureService } from '@app/core/services/feature.service';
import { OidcAuthService } from '@app/core/services/oidc-auth.service';
import { NotificationService } from '@app/core/notifications/notification.service';
import { BuySellPlanEmployee } from '@app/modules/time-off/models/buysell.model';
import { BuySellService } from '@app/modules/time-off/services/buy-sell.service';
import { SecurityProtectedBase } from '@app/shared/components/security-protected/security-protected';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { filter, merge } from 'rxjs';
import { EmployeeBuySellRequestComponent } from '../employee-buy-sell-request/employee-buy-sell-request.component';

@Component({
  selector: 'app-employee-buy-sell',
  templateUrl: './employee-buy-sell.component.html',
  styleUrls: ['./employee-buy-sell.component.scss']
})
export class EmployeeBuySellComponent extends SecurityProtectedBase implements OnInit {
  @Input() employeeId: string;
  @Input() showTitle: boolean = true;
  category: string = "Buy-Sell-Plans";
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 20;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  formValid: any;
  sortString: string;
  isLoadingPermissions: boolean = true;
  filterString: string;
  gridId: string;
  getRequest: any;
  searchValue: string;
  searchFilterString: string;
  dialogRef: any;

  public columns: any[] = [
    { field: "plan", subField: "name", title: "Plan", localizationCode: "BuySellPlan" },
    { field: "status", subField: "name", title: "Status", type: 'chip', chipValue: ['Approved', 'Denied', 'Pending', 'Available'], localizationCode: "BuySellStatus" },
    { field: "publishedOn", title: "Published On", type: "longDate", localizationCode: "BuySellPublishedOn" },
    { field: "publishedBy", title: "Published By", type: "employee", localizationCode: "BuySellPublishedBy" }
  ];

  filterCategories: any[] = [
    { field: 'plan.name', title: 'Plan Name', localizationCode: "BuySellPlan", dataType: 'String', tableId: "tfi_PlanName" },
    { field: 'status.name', title: 'Status', localizationCode: "BuySellStatus", dataType: 'String', tableId: "tfi_StatusName" },
    { field: "publishedOn", title: "Published On", localizationCode: "BuySellPublishedOn", type: "Date", dataType: 'Date' },
  ];

  sortableColumns: any[] = [
    { field: "plan", sortValue: "plan.name" },
    { field: "status", sortValue: "status.name" },
    { field: "publishedOn", sortValue: "publishedOn" },
    { field: "publishedBy", sortValue: ["publishedBy.FirstName", "publishedBy.LastName"] }
  ];

  constructor(
    public translate: TranslateService,
    private dialog: MatDialog,
    private overlay: Overlay,
    private buySellService: BuySellService,
    featureService: FeatureService,
    private notificationService: NotificationService,
    private oidcAuthService: OidcAuthService,
    private destroyRef: DestroyRef,
  ) {
    super(featureService, features.BUY_SELL);

    this.gridId = `employeeBuySell_${this.employeeId}`;
  }

  ngOnInit(): void {
    if(this.showTitle === false) {
      this.category = null;
    }
    if (this.hasFeatureAccess) {
      this.getBuySellPlanEmployees();

      // These events only go to the requesting employee, so only refresh the user's own list.
      merge(
        this.notificationService.on('buysell.request.approved'),
        this.notificationService.on('buysell.request.denied')
      )
        .pipe(
          filter(() => this.employeeId === this.oidcAuthService.userProfile?.userId),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => {
          this.getRequest?.unsubscribe();
          this.getBuySellPlanEmployees();
        });
    }
  }

  getBuySellPlanEmployees() {
    this.isLoading = true;

    // Combine filterString and searchFilterString if both exist
    let baseFilter = `(employee.id = "${this.employeeId}") AND (published = "true")`;
    let combinedFilter;

    if (this.filterString && this.searchFilterString) {
      combinedFilter = `${baseFilter} AND (${this.filterString}) AND (${this.searchFilterString})`;
    } else if (this.filterString) {
      combinedFilter = `${baseFilter} AND (${this.filterString})`;
    } else if (this.searchFilterString) {
      combinedFilter = `${baseFilter} AND (${this.searchFilterString})`;
    } else {
      combinedFilter = baseFilter;
    }

    this.getRequest = this.buySellService.getBuySellPlanEmployees(this.pageSize, this.skip, combinedFilter, this.sortString, null).subscribe({
      next: (data) => {
        this.gridDataResult = data;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
      }
    });
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `(plan.name like "${variant}")`;
        }
      });

      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getBuySellPlanEmployees();
  }

  openDialog(buySellPlanEmployee: BuySellPlanEmployee) {
    let dialogRef;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.scrollStrategy = this.overlay.scrollStrategies.block();

    dialogConfig.data = {
      employeeId: this.employeeId,
      buySellPlanEmpId: buySellPlanEmployee.id,
      buySellPlan: buySellPlanEmployee.plan
    };
    
    dialogRef = this.dialog.open(EmployeeBuySellRequestComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.getBuySellPlanEmployees();
        }
      }
    );
  }

}
